// Ecran ouvert par le lien d'invitation d'un coach (/rejoindre/<jeton>), une
// fois connecte : la personne confirme qu'elle devient athlete de ce coach,
// dans son club (LienInvitationService.rejoindre cote serveur). Une personne
// qui n'avait pas encore choisi son profil est inscrite comme athlete.
import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { lireInvitationPublique, rejoindreCoach, type LienInvitation, type Moi } from './moi.ts';

export default function RejoindreCoach({ jeton, moi, terminer, handleLogout }: {
  jeton: string;
  moi: Moi | null;
  // Retour a l'application (adresse /).
  terminer: () => void;
  handleLogout: () => void;
}) {
  const [invitation, setInvitation] = React.useState<LienInvitation | null | undefined>(undefined);
  const [enCours, setEnCours] = React.useState(false);
  const [erreur, setErreur] = React.useState<string | null>(null);

  React.useEffect(() => {
    lireInvitationPublique(jeton).then(setInvitation).catch(() => setInvitation(null));
  }, [jeton]);

  const rejoindre = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      await rejoindreCoach(jeton);
      terminer();
    } catch (e) {
      setErreur((e as Error).message);
      setEnCours(false);
    }
  };

  const coach = invitation?.coach || 'Un coach';
  const dejaSuivi = !!moi?.coach && moi.coach === invitation?.coach;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        backgroundImage: 'url(/background.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Box
        sx={{
          width: 'min(100%, 520px)',
          p: { xs: 2.5, sm: 4 },
          borderRadius: 3,
          color: '#fff',
          background: 'rgba(15, 20, 30, 0.72)',
          backdropFilter: 'blur(6px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
        }}
      >
        {invitation === undefined && <CircularProgress color="inherit" />}

        {invitation === null && (
          <>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Lien d'invitation expiré</Typography>
            <Typography sx={{ mb: 3, opacity: 0.85 }}>
              Ce lien n'est plus valide : demande un nouveau lien à ton coach.
            </Typography>
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" onClick={terminer}>Continuer</Button>
            </Stack>
          </>
        )}

        {invitation && (
          <>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Bienvenue{moi?.nom ? ' ' + moi.nom : ''} !
            </Typography>
            <Typography sx={{ mb: 2 }}>
              <b>{coach}</b> t'invite à le rejoindre comme athlète
              {invitation.club ? <> au club <b>{invitation.club}</b></> : null}.
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, opacity: 0.8 }}>
              En acceptant, tu deviens son athlète : il établit ton calendrier d'entraînement et voit ta VMA et tes
              allures. Tu pourras le quitter à tout moment depuis la page Mes préférences.
            </Typography>
            {dejaSuivi && <Alert severity="info" sx={{ mb: 2 }}>Tu es déjà suivi par ce coach.</Alert>}
            {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              {moi && !moi.role && (
                <Button color="inherit" onClick={handleLogout} disabled={enCours}>Se déconnecter</Button>
              )}
              <Button color="inherit" onClick={terminer} disabled={enCours}>
                {dejaSuivi ? 'Continuer' : 'Ignorer'}
              </Button>
              {!dejaSuivi && (
                <Button variant="contained" onClick={rejoindre} disabled={enCours || !moi}>
                  Rejoindre {coach}
                </Button>
              )}
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
}
